import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, RotateCcw, Bot, FlaskConical, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { workerApi } from '../../../services/workerApi';
import type { Platform } from '../../../types/platform';

/* ─── Types ─────────────────────────────────────────────────────────────── */

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  ts: Date;
  error?: boolean;
  content_type?: 'text' | 'image' | 'carousel';
  metadata?: { title?: string; urls?: string[]; elements?: { title: string; image_url: string }[] };
}

interface Props {
  account: Platform;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function makeId() {
  return Math.random().toString(36).slice(2);
}

function formatTime(d: Date) {
  return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

/** Three animated dots typing indicator */
function TypingIndicator() {
  return (
    <div className="flex items-end gap-2 mb-3">
      <div className="w-7 h-7 rounded-full bg-[#3390ec] flex items-center justify-center flex-shrink-0">
        <Bot size={14} className="text-white" />
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
        <span className="flex gap-1 items-center h-4">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:0ms]" />
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:150ms]" />
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:300ms]" />
        </span>
      </div>
    </div>
  );
}

/** A single chat bubble */
function Bubble({ msg, botAvatar }: { msg: ChatMessage; botAvatar?: string }) {
  const isBot = msg.role === 'assistant';

  // Image or carousel bubble
  if (isBot && (msg.content_type === 'image' || msg.content_type === 'carousel')) {
    const urls = msg.metadata?.urls || (msg.content ? [msg.content] : []);
    const title = msg.metadata?.title || '';
    return (
      <div className="flex items-end gap-2 mb-3">
        {botAvatar ? (
          <img src={botAvatar} alt="bot" className="w-7 h-7 rounded-full flex-shrink-0 object-cover" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[#3390ec] flex items-center justify-center flex-shrink-0">
            <Bot size={14} className="text-white" />
          </div>
        )}
        <div className="max-w-[70%] flex flex-col gap-1">
          <div className="flex gap-1.5 flex-wrap">
            {urls.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={title || 'product'}
                className="rounded-xl border border-gray-200 shadow-sm max-w-[160px] max-h-[160px] object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ))}
          </div>
          {title && <span className="text-xs text-gray-500 pl-1">{title}</span>}
          <span className="text-[10px] text-gray-400 pl-1">{formatTime(msg.ts)}</span>
        </div>
      </div>
    );
  }

  if (isBot) {
    return (
      <div className="flex items-end gap-2 mb-3">
        {/* Bot avatar */}
        {botAvatar ? (
          <img
            src={botAvatar}
            alt="bot"
            className="w-7 h-7 rounded-full flex-shrink-0 object-cover"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[#3390ec] flex items-center justify-center flex-shrink-0">
            <Bot size={14} className="text-white" />
          </div>
        )}

        <div className="max-w-[70%] flex flex-col gap-0.5">
          {msg.error ? (
            <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-600 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm">
              <AlertCircle size={13} />
              {msg.content}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 text-gray-800 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm leading-relaxed shadow-sm whitespace-pre-wrap">
              {msg.content}
            </div>
          )}
          <span className="text-[10px] text-gray-400 pl-1">{formatTime(msg.ts)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-end justify-end gap-2 mb-3">
      <div className="max-w-[70%] flex flex-col items-end gap-0.5">
        <div className="bg-[#3390ec] text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm leading-relaxed shadow-sm whitespace-pre-wrap">
          {msg.content}
        </div>
        <span className="text-[10px] text-gray-400 pr-1">{formatTime(msg.ts)}</span>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */

export const TestChatTab: React.FC<Props> = ({ account }) => {
  const { t } = useTranslation('chatbot');
  const botName = account.name || t('testBotFallback');
  const botAvatar = account.avatar;

  const makeWelcome = useCallback(
    (): ChatMessage => ({
      id: makeId(),
      role: 'assistant',
      content: t('testWelcome', { botName }),
      ts: new Date(),
    }),
    [botName, t],
  );

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load persisted history from worker DB on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await workerApi.getTestChatHistory(account.platform_id);
        if (cancelled) return;
        if (res.messages.length > 0) {
          setMessages(
            res.messages.map((m: any) => ({
              id: m.id,
              role: m.role as 'user' | 'assistant',
              content: m.content,
              content_type: m.content_type || 'text',
              metadata: m.metadata,
              ts: new Date(m.ts * 1000),
            }))
          );
        } else {
          setMessages([makeWelcome()]);
        }
      } catch {
        if (!cancelled) setMessages([makeWelcome()]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.platform_id]);

  // Auto-scroll on new message or typing indicator
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const handleReset = async () => {
    try { await workerApi.clearTestChatHistory(account.platform_id); } catch {}
    setMessages([makeWelcome()]);
    setInput('');
    setThinking(false);
    inputRef.current?.focus();
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || thinking || historyLoading) return;

    // Append user bubble
    const userMsg: ChatMessage = { id: makeId(), role: 'user', content: text, ts: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setThinking(true);

    try {
      const data = await workerApi.testChat(
        account.platform_id,
        text,
      );
      const reply = data?.reply ?? 'Không có phản hồi.';
      const media = data?.media || [];

      const newMessages: ChatMessage[] = [
        { id: makeId(), role: 'assistant', content: reply, ts: new Date() },
      ];

      // Add media bubbles (images / carousel) just like Facebook flow
      for (const m of media) {
        if (m.type === 'image') {
          newMessages.push({
            id: makeId(), role: 'assistant', content: m.url,
            ts: new Date(), content_type: 'image',
            metadata: { title: m.title, urls: [m.url] },
          });
        } else if (m.type === 'carousel') {
          const urls = (m.elements || []).map((el: any) => el.image_url);
          const title = m.elements?.[0]?.title || '';
          newMessages.push({
            id: makeId(), role: 'assistant', content: urls[0] || '',
            ts: new Date(), content_type: 'carousel',
            metadata: { title, urls, elements: m.elements },
          });
        }
      }

      setMessages(prev => [...prev, ...newMessages]);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        t('testConnectionError');
      setMessages(prev => [
        ...prev,
        { id: makeId(), role: 'assistant', content: detail, ts: new Date(), error: true },
      ]);
    } finally {
      setThinking(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          {botAvatar ? (
            <img src={botAvatar} alt="bot" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[#3390ec]/10 flex items-center justify-center">
              <Bot size={18} className="text-[#3390ec]" />
            </div>
          )}
          <div>
            <p className="text-sm font-semibold text-gray-900">{botName}</p>
            <p className="text-xs text-gray-400">{t('testEnvironmentNote')}</p>
          </div>
        </div>

        <button
          onClick={handleReset}
          title={t('testReset')}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <RotateCcw size={13} />
          {t('testReset')}
        </button>
      </div>

      {/* ── Notice banner ── */}
      <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2 text-xs text-amber-700 flex-shrink-0">
        <FlaskConical size={13} />
        {t('testConfigNotice')}
      </div>

      {/* ── Message list ── */}
      <div className="flex-1 overflow-y-auto px-5 py-4 bg-gray-50 min-h-0">
        {historyLoading ? (
          <div className="flex items-center justify-center h-full text-sm text-gray-400">{t('loading')}</div>
        ) : (
          <>
            {messages.map(msg => (
              <Bubble key={msg.id} msg={msg} botAvatar={botAvatar} />
            ))}
            {thinking && <TypingIndicator />}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {/* ── Input area ── */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3">
        <div className="flex items-end gap-2 bg-gray-100 rounded-2xl px-4 py-2">
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('testNoReply')}
            disabled={thinking || historyLoading}
            className="flex-1 bg-transparent resize-none text-sm text-gray-800 placeholder-gray-400 outline-none max-h-28 min-h-[1.5rem] leading-relaxed disabled:opacity-50"
          />
          <button
            aria-label="Gửi tin nhắn"
            onClick={sendMessage}
            disabled={!input.trim() || thinking || historyLoading}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-[#3390ec] text-white flex-shrink-0 transition-all hover:bg-[#2280dc] disabled:opacity-40 disabled:cursor-not-allowed mb-0.5"
          >
            <Send size={14} />
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5 text-center">
          Enter để gửi · Shift+Enter xuống dòng
        </p>
      </div>
    </div>
  );
};
